/**
 * Outil « pH et acidité » (Boîte à outils, physique-chimie 3e et Tle).
 *
 * Explorer :
 * - Échelle : une solution de la vie courante (ou un pH choisi) sur l'échelle de pH, couleur du
 *   papier pH ; bécher d'ions H⁺ et HO⁻ (3e) ou [H₃O⁺] et [HO⁻] sur une échelle logarithmique (Tle).
 * - Dilution : on dilue 10 fois, encore et encore, de l'acide chlorhydrique ou de la soude ;
 *   le pH tend vers 7 sans le franchir.
 * - Prédominance (Tle) : diagramme de prédominance et courbes de distribution d'un couple
 *   (acide éthanoïque, ion ammonium, BBT, glycine) ; couleur de l'indicateur.
 * Défi : une question par sorte selon le niveau. Logique : `ph.ts`.
 */

import { useState, type SVGProps } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import {
  Note,
  PRIMARY_BUTTON,
  ParamSlider,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
} from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { McqChallenge } from '../../ui/mcq-challenge';
import { RichText } from '../../ui/rich-text';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  COUPLES,
  DILUTION_START,
  MAX_DILUTIONS,
  advanced,
  conc,
  dilutionLines,
  dilutionSeries,
  fr,
  fractions,
  h3o,
  ho,
  indicatorColor,
  ionDots,
  makeQuiz,
  natureOf,
  partsFor,
  phText,
  pow10,
  predominanceLines,
  predominantIndex,
  quizKinds,
  scaleLines,
  sci,
  startConc,
  universalColor,
  SOLUTIONS,
  type Couple,
  type Part,
  type Solute,
} from './ph';

const W = 340;
const INK = 'var(--color-ink-700)';
const MUTED = 'var(--color-ink-500)';
const ACID = 'var(--color-error-500)';
const BASE = 'var(--color-primary-500)';
const MID = 'var(--color-warning-500)';
const X0 = 22;
const X1 = W - 22;
const xOf = (pH: number) => X0 + (pH / 14) * (X1 - X0);

function Lines({ lines }: { lines: string[] }) {
  return (
    <Note>
      {lines.map((l) => (
        <span key={l} className="mb-1 block last:mb-0" data-testid="ph-law">
          <RichText>{l}</RichText>
        </span>
      ))}
    </Note>
  );
}

function Readout({ rows }: { rows: Array<[string, string]> }) {
  return (
    <table className="w-full text-sm" data-testid="ph-readings">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-ink-100 border-b last:border-0">
            <th scope="row" className="text-ink-600 py-1.5 pr-2 text-left font-normal">
              <RichText>{k}</RichText>
            </th>
            <td className="text-ink-900 py-1.5 text-right font-semibold tabular-nums">
              {frenchSpaces(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** « pK_A » en SVG : indice en tspan */
function SvgSub({ text, ...props }: { text: string } & SVGProps<SVGTextElement>) {
  const parts = text.split(/([A-Za-z])_([A-Za-z0-9]+)/);
  return (
    <HaloText {...props}>
      {parts.map((p, i) =>
        i % 3 === 2 ? (
          <tspan key={i} baselineShift="sub" fontSize="0.75em">
            {p}
          </tspan>
        ) : (
          <tspan key={i}>{p}</tspan>
        ),
      )}
    </HaloText>
  );
}

/* ---------- échelle ---------- */

/** barre 0 → 14 aux couleurs du papier pH, repère au pH ; y de 0 à 76 */
function ScaleBar({ pH, id }: { pH: number; id: string }) {
  const x = xOf(pH);
  return (
    <g>
      <defs>
        <linearGradient id={id} x1="0" x2="1" y1="0" y2="0">
          {Array.from({ length: 15 }, (_, i) => (
            <stop key={i} offset={i / 14} stopColor={universalColor(i)} />
          ))}
        </linearGradient>
      </defs>
      <rect x={X0} y={24} width={X1 - X0} height={16} rx={8} fill={`url(#${id})`} />
      {Array.from({ length: 15 }, (_, i) => (
        <text
          key={i}
          x={xOf(i)}
          y={54}
          textAnchor="middle"
          fontSize={9}
          fill={i === 7 ? INK : MUTED}
          fontWeight={i === 7 ? 700 : 400}
        >
          {i}
        </text>
      ))}
      <text x={X0} y={70} fontSize={10} fill={ACID} fontWeight={600}>
        ← acide
      </text>
      <text x={xOf(7)} y={70} textAnchor="middle" fontSize={10} fill={INK} fontWeight={600}>
        neutre
      </text>
      <text x={X1} y={70} textAnchor="end" fontSize={10} fill={BASE} fontWeight={600}>
        basique →
      </text>
      <line x1={x} y1={19} x2={x} y2={45} stroke="white" strokeWidth={6} strokeLinecap="round" />
      <line x1={x} y1={19} x2={x} y2={45} stroke={INK} strokeWidth={3} strokeLinecap="round" />
      <HaloText
        x={Math.min(X1 - 26, Math.max(X0 + 26, x))}
        y={13}
        textAnchor="middle"
        fontSize={11}
        fontWeight={700}
        fill={INK}
      >
        {`pH ${phText(pH)}`}
      </HaloText>
    </g>
  );
}

/** positions fixes (pseudo-hasard) dans le liquide du bécher */
function spots(n: number, seed: number, box: { x: number; y: number; w: number; h: number }) {
  const out: Array<[number, number]> = [];
  let s = seed;
  const rnd = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  let tries = 0;
  while (out.length < n && tries < 2000) {
    tries++;
    const p: [number, number] = [box.x + rnd() * box.w, box.y + rnd() * box.h];
    if (out.every(([a, b]) => (a - p[0]) ** 2 + (b - p[1]) ** 2 > 22 ** 2)) out.push(p);
  }
  return out;
}

const DOTS = spots(34, 7, { x: 58, y: 118, w: 150, h: 96 });

function Beaker({ pH }: { pH: number }) {
  const { h, ho: o } = ionDots(pH);
  const ions = [...Array(h).fill('H⁺'), ...Array(o).fill('HO⁻')] as string[];
  // on entremêle : positions fixes, ordre des ions mélangé régulièrement
  const order = ions.map((_, i) => (i * 13) % DOTS.length);
  return (
    <g data-testid="ph-beaker">
      <rect x={44} y={104} width={178} height={122} fill={universalColor(pH)} opacity={0.14} />
      <path
        d="M 40 86 L 44 92 L 44 226 Q 44 232 50 232 L 216 232 Q 222 232 222 226 L 222 92 L 226 86"
        fill="none"
        stroke={INK}
        strokeWidth={2}
      />
      <line x1={44} y1={104} x2={222} y2={104} stroke={MUTED} strokeDasharray="4 3" />
      {ions.map((ion, i) => {
        const [x, y] = DOTS[order[i]!]!;
        const acid = ion === 'H⁺';
        return (
          <g key={i} data-testid={acid ? 'ph-ion-h' : 'ph-ion-ho'}>
            <circle cx={x} cy={y} r={10} fill={acid ? ACID : BASE} opacity={0.9} />
            <text
              x={x}
              y={y + 3.5}
              textAnchor="middle"
              fontSize={8.5}
              fontWeight={700}
              fill="white"
            >
              {ion}
            </text>
          </g>
        );
      })}
      <rect
        x={262}
        y={110}
        width={34}
        height={84}
        rx={3}
        fill={universalColor(pH)}
        stroke={INK}
        strokeWidth={1}
      />
      <text x={279} y={210} textAnchor="middle" fontSize={10} fill={MUTED}>
        papier pH
      </text>
      <text x={133} y={248} textAnchor="middle" fontSize={9.5} fill={MUTED}>
        schéma : les nombres d’ions ne sont pas à l’échelle
      </text>
    </g>
  );
}

/** [H₃O⁺] et [HO⁻] sur une échelle logarithmique de 10⁻¹⁴ à 1 mol·L⁻¹ */
function LogBars({ pH }: { pH: number }) {
  const len = (log: number) => ((log + 14) / 14) * (X1 - X0);
  const bars: Array<[string, number, string, number]> = [
    ['[H₃O⁺]', -pH, ACID, h3o(pH)],
    ['[HO⁻]', pH - 14, BASE, ho(pH)],
  ];
  return (
    <g data-testid="ph-logbars">
      {bars.map(([name, log, color, v], k) => {
        const y = 104 + k * 58;
        const l = len(log);
        return (
          <g key={name}>
            <text x={X0} y={y} fontSize={11} fontWeight={700} fill={color}>
              {name}
            </text>
            <rect x={X0} y={y + 8} width={X1 - X0} height={16} rx={4} fill="var(--color-ink-100)" />
            <rect x={X0} y={y + 8} width={Math.max(2, l)} height={16} rx={4} fill={color} />
            <HaloText x={X1} y={y} textAnchor="end" fontSize={11} fontWeight={600} fill={INK}>
              {`${sci(v)} mol·L⁻¹`}
            </HaloText>
          </g>
        );
      })}
      {[-14, -7, 0].map((e) => (
        <text
          key={e}
          x={X0 + len(e)}
          y={204}
          textAnchor={e === -14 ? 'start' : e === 0 ? 'end' : 'middle'}
          fontSize={9.5}
          fill={MUTED}
        >
          {e === 0 ? '1' : pow10(e)}
        </text>
      ))}
      <text x={W / 2} y={224} textAnchor="middle" fontSize={9.5} fill={MUTED}>
        échelle logarithmique (mol·L⁻¹) : un cran de pH = × 10
      </text>
    </g>
  );
}

function ScaleView({ rank }: { rank: number | null }) {
  const adv = advanced(rank);
  const [pH, setPH] = useState(SOLUTIONS[1]!.pH);
  // la solution affichée suit le pH ; sinon on part de la plus proche
  const match = SOLUTIONS.findIndex((s) => s.pH === pH);
  const near = SOLUTIONS.reduce(
    (b, s, i) => (Math.abs(s.pH - pH) < Math.abs(SOLUTIONS[b]!.pH - pH) ? i : b),
    0,
  );
  const n = natureOf(pH);
  const rows: Array<[string, string]> = [
    ['pH', phText(pH)],
    ['Nature', n === 'neutre' ? 'neutre' : `${n}${Math.abs(pH - 7) > 5 ? ' (très)' : ''}`],
  ];
  if (adv) {
    rows.push(['[H₃O⁺] = c° × 10⁻ᵖᴴ', conc(h3o(pH))]);
    rows.push(['[HO⁻] = K_e ÷ [H₃O⁺]', conc(ho(pH))]);
  } else {
    rows.push([
      'Ions les plus nombreux',
      n === 'neutre' ? 'autant de H⁺ que de HO⁻' : n === 'acide' ? 'H⁺' : 'HO⁻',
    ]);
  }
  return (
    <div className="space-y-4">
      <svg
        viewBox={`0 0 ${W} 256`}
        className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
        role="img"
        aria-label={`Échelle de pH : pH ${phText(pH)}, solution ${n}`}
        data-testid="ph-figure"
      >
        <ScaleBar pH={pH} id="ph-scale-grad" />
        {adv ? <LogBars pH={pH} /> : <Beaker pH={pH} />}
      </svg>
      <Stepper
        label="Solution"
        value={match >= 0 ? match : near}
        min={0}
        max={SOLUTIONS.length - 1}
        onChange={(i) => setPH(SOLUTIONS[i]!.pH)}
        format={(i) => (i === match ? SOLUTIONS[i]!.label : 'pH choisi')}
      />
      <ParamSlider
        name="pH"
        ariaLabel="pH"
        value={pH}
        min={0}
        max={14}
        step={0.1}
        onChange={setPH}
        format={phText}
      />
      <Readout rows={rows} />
      <Lines lines={scaleLines(pH, rank)} />
    </div>
  );
}

/* ---------- dilution ---------- */

function DilutionChart({ series }: { series: number[] }) {
  const H = 230;
  const L = 34;
  const R = W - 14;
  const T = 24;
  const B = H - 34;
  const X = (k: number) => L + (k / MAX_DILUTIONS) * (R - L);
  const Y = (p: number) => B - (p / 14) * (B - T);
  const last = series.length - 1;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label={`pH après ${last} dilutions : ${fr(series[last]!, 2)}`}
      data-testid="ph-dilution-chart"
    >
      <rect x={L} y={Y(7)} width={R - L} height={Y(0) - Y(7)} fill={ACID} opacity={0.06} />
      <rect x={L} y={Y(14)} width={R - L} height={Y(7) - Y(14)} fill={BASE} opacity={0.06} />
      <line x1={L} y1={B} x2={R} y2={B} stroke="var(--color-ink-300)" />
      <line x1={L} y1={T} x2={L} y2={B} stroke="var(--color-ink-300)" />
      {[0, 7, 14].map((p) => (
        <text key={p} x={L - 6} y={Y(p) + 3.5} textAnchor="end" fontSize={10} fill={MUTED}>
          {p}
        </text>
      ))}
      <text x={L - 6} y={12} textAnchor="end" fontSize={10} fill={INK} fontWeight={700}>
        pH
      </text>
      <line x1={L} y1={Y(7)} x2={R} y2={Y(7)} stroke={INK} strokeDasharray="5 4" />
      <HaloText x={L + 4} y={Y(7) + (series[0]! < 7 ? -5 : 13)} fontSize={10} fill={INK}>
        pH = 7 : neutre
      </HaloText>
      {Array.from({ length: MAX_DILUTIONS + 1 }, (_, k) => (
        <text key={k} x={X(k)} y={B + 14} textAnchor="middle" fontSize={10} fill={MUTED}>
          {k}
        </text>
      ))}
      <text x={R} y={H - 6} textAnchor="end" fontSize={10} fill={MUTED}>
        nombre de dilutions au dixième
      </text>
      <polyline
        points={series.map((p, k) => `${X(k)},${Y(p)}`).join(' ')}
        fill="none"
        stroke={INK}
        strokeWidth={1.5}
      />
      {series.map((p, k) => (
        <circle
          key={k}
          cx={X(k)}
          cy={Y(p)}
          r={k === last ? 7 : 5}
          fill={universalColor(p)}
          stroke={INK}
          strokeWidth={k === last ? 2 : 1}
          data-testid="ph-dilution-point"
        />
      ))}
      <HaloText
        x={Math.min(R - 30, X(last) + 10)}
        y={Y(series[last]!) + (series[last]! < 7 ? 18 : -10)}
        fontSize={11}
        fontWeight={700}
        fill={INK}
      >
        {`pH ${fr(series[last]!, 2)}`}
      </HaloText>
    </svg>
  );
}

function DilutionView({ rank }: { rank: number | null }) {
  const adv = advanced(rank);
  const [kind, setKind] = useState<Solute>('acid');
  const [n, setN] = useState(0);
  const pH0 = DILUTION_START[kind].pH;
  const series = dilutionSeries(kind, pH0, n);
  const rows: Array<[string, string]> = [
    ['Dilutions au dixième', String(n)],
    ['Volume multiplié par', fr(10 ** n, 0)],
    ['pH', fr(series[n]!, 2)],
  ];
  if (adv) rows.splice(2, 0, ['Concentration c', conc(startConc(kind, pH0) / 10 ** n)]);
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Solution de départ"
        value={kind}
        options={(['acid', 'base'] as const).map((k) => ({
          value: k,
          label: DILUTION_START[k].label,
        }))}
        onChange={(k) => {
          setKind(k);
          setN(0);
        }}
      />
      <DilutionChart series={series} />
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          className={`${PRIMARY_BUTTON} disabled:opacity-40`}
          disabled={n >= MAX_DILUTIONS}
          onClick={() => setN(n + 1)}
        >
          Diluer 10 fois
        </button>
        <button type="button" className={SECONDARY_BUTTON} onClick={() => setN(0)}>
          Recommencer
        </button>
      </div>
      <Readout rows={rows} />
      <Lines lines={dilutionLines(kind, series, rank)} />
    </div>
  );
}

/* ---------- prédominance ---------- */

/** couleurs des formes : celles de l'indicateur s'il y en a ; sinon rouge, (ambre), bleu */
const speciesColors = (c: Couple) =>
  c.colors
    ? c.colors.map(([, hex]) => hex)
    : c.species.length === 2
      ? [ACID, BASE]
      : [ACID, MID, BASE];

function PredominanceFigure({ pH, coupleId }: { pH: number; coupleId: string }) {
  const c = COUPLES.find((x) => x.id === coupleId)!;
  const colors = speciesColors(c);
  const H = 214;
  const L = 44;
  const R = W - 16;
  const T = 66;
  const B = 184;
  const X = (p: number) => L + (p / 14) * (R - L);
  const Y = (f: number) => B - f * (B - T);
  const bounds = [0, ...c.pKas, 14];
  const curve = (i: number) =>
    Array.from({ length: 141 }, (_, k) => {
      const p = k / 10;
      return `${X(p)},${Y(fractions(p, c.pKas)[i]!)}`;
    }).join(' ');
  const f = fractions(pH, c.pKas);
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label={`Diagramme de prédominance du couple ${c.species.join(' / ')} à pH ${phText(pH)}`}
      data-testid="ph-predominance"
    >
      {/* diagramme de prédominance : la forme qui l'emporte sur chaque intervalle */}
      {c.species.map((s, i) => (
        <rect
          key={s}
          x={X(bounds[i]!)}
          y={30}
          width={X(bounds[i + 1]!) - X(bounds[i]!)}
          height={14}
          fill={colors[i]}
          opacity={0.8}
        />
      ))}
      {c.pKas.map((k, i) => (
        <g key={k}>
          <line x1={X(k)} y1={26} x2={X(k)} y2={48} stroke={INK} strokeWidth={2} />
          <SvgSub
            text={c.pKas.length > 1 ? `pK_A${i + 1} ${phText(k)}` : `pK_A ${phText(k)}`}
            x={X(k)}
            y={20}
            textAnchor="middle"
            fontSize={10}
            fontWeight={600}
            fill={INK}
          />
        </g>
      ))}
      {/* courbes de distribution (proportions) */}
      <line x1={L} y1={B} x2={R} y2={B} stroke="var(--color-ink-300)" />
      <line x1={L} y1={T} x2={L} y2={B} stroke="var(--color-ink-300)" />
      <line
        x1={L}
        y1={Y(0.5)}
        x2={R}
        y2={Y(0.5)}
        stroke="var(--color-ink-200)"
        strokeDasharray="3 3"
      />
      {[0, 0.5, 1].map((v) => (
        <text key={v} x={L - 6} y={Y(v) + 3.5} textAnchor="end" fontSize={9.5} fill={MUTED}>
          {`${v * 100} %`}
        </text>
      ))}
      {c.species.map((s, i) => (
        <polyline key={s} points={curve(i)} fill="none" stroke={colors[i]} strokeWidth={2.2} />
      ))}
      {[0, 7, 14].map((p) => (
        <text key={p} x={X(p)} y={B + 13} textAnchor="middle" fontSize={10} fill={MUTED}>
          {p}
        </text>
      ))}
      <text x={R} y={H - 4} textAnchor="end" fontSize={10} fill={MUTED}>
        pH →
      </text>
      <line x1={X(pH)} y1={26} x2={X(pH)} y2={B} stroke={INK} strokeDasharray="2 3" />
      <path d={`M ${X(pH) - 6} 54 L ${X(pH) + 6} 54 L ${X(pH)} 47 Z`} fill={INK} />
      {f.map((v, i) => (
        <circle
          key={i}
          cx={X(pH)}
          cy={Y(v)}
          r={4.5}
          fill={colors[i]}
          stroke="white"
          strokeWidth={1.5}
        />
      ))}
    </svg>
  );
}

function PredominanceView() {
  const [coupleId, setCoupleId] = useState(COUPLES[0]!.id);
  const [pH, setPH] = useState(7);
  const c = COUPLES.find((x) => x.id === coupleId)!;
  const f = fractions(pH, c.pKas);
  const i = predominantIndex(pH, c.pKas);
  const colors = speciesColors(c);
  const tint = indicatorColor(pH, c);
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Couple acide-base"
        value={coupleId}
        options={COUPLES.map((x) => ({ value: x.id, label: x.label }))}
        onChange={setCoupleId}
      />
      <PredominanceFigure pH={pH} coupleId={coupleId} />
      <ParamSlider
        name="pH"
        ariaLabel="pH"
        value={pH}
        min={0}
        max={14}
        step={0.1}
        onChange={setPH}
        format={phText}
      />
      {tint && (
        <p className="text-ink-700 flex items-center gap-3 text-sm" data-testid="ph-indicator">
          <span
            className="border-ink-300 h-10 w-7 shrink-0 rounded-b-full border"
            style={{ background: tint }}
            aria-hidden
          />
          {frenchSpaces(`Teinte du bleu de bromothymol à pH ${phText(pH)}`)}
        </p>
      )}
      <ul className="space-y-1 text-sm" data-testid="ph-species">
        {c.species.map((s, k) => (
          <li
            key={s}
            className={`flex items-center justify-between gap-2 rounded-xl px-2 py-1.5 ${i === k ? 'bg-ink-50 font-semibold' : ''}`}
          >
            <span className="flex items-center gap-2">
              <span
                className="h-3 w-3 shrink-0 rounded-full"
                style={{ background: colors[k] }}
                aria-hidden
              />
              <span className="text-ink-900">{s}</span>
              {c.colors && <span className="text-ink-500">({c.colors[k]![0]})</span>}
            </span>
            <span className="text-ink-900 tabular-nums">
              {frenchSpaces(`${fr(100 * f[k]!, 1)} %`)}
            </span>
          </li>
        ))}
      </ul>
      <Lines lines={predominanceLines(pH, c)} />
    </div>
  );
}

function Explore({ rank }: { rank: number | null }) {
  const parts = partsFor(rank);
  const [part, setPart] = useState<Part>(parts[0]!.id);
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie"
        value={part}
        options={parts.map((p) => ({ value: p.id, label: p.label }))}
        onChange={setPart}
      />
      {part === 'scale' && <ScaleView rank={rank} />}
      {part === 'dilution' && <DilutionView rank={rank} />}
      {part === 'predominance' && <PredominanceView />}
    </div>
  );
}

export function PhTool({
  level = null,
  seed,
}: {
  /** niveau : parties et notions selon la classe (3e, Tle) */
  level?: string | null;
  /** graine du défi (tests) */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore rank={rank} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <McqChallenge kinds={quizKinds(rank)} make={makeQuiz} seed={seed} testId="ph" rich />
      </div>
    </div>
  );
}
